import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { LAYOUT_TEMPLATES, createMemorialBoxes } from './LayoutTemplates';

const LayoutManager = () => {
  const navigate = useNavigate();
  const [layouts, setLayouts] = useState([]);
  const [globalTemplates, setGlobalTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [isMasterAdmin, setIsMasterAdmin] = useState(false);

  useEffect(() => {
    // Check if user is master admin
    const userData = localStorage.getItem('userData');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setIsMasterAdmin(user.is_staff === true);
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }

    loadLayouts();
    loadGlobalTemplates();
  }, []);

  const loadLayouts = async () => {
    try {
      const response = await api.get('/shul/display-layouts/');
      setLayouts(response.layouts || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading layouts:', error);
      setError('Failed to load layouts');
      setLoading(false);
    }
  };

  const loadGlobalTemplates = async () => {
    try {
      const response = await api.get('/shul/global-templates/');
      setGlobalTemplates(response.templates || []);
    } catch (error) {
      console.error('Error loading global templates:', error);
      // Non-fatal error, just log it
    }
  };

  const createNewLayout = async () => {
    try {
      const layoutName = prompt('Enter a name for your new layout:');
      if (!layoutName) return;

      const response = await api.post('/shul/display-layouts/', {
        name: layoutName,
        layout_config: {
          version: 'v2',
          boxes: createMemorialBoxes(), // Start with memorial boxes
          globalSettings: {
            backgroundType: 'default',
            backgroundColor: '#000000',
            backgroundImage: null,
            headerBgColor: '#162A45',
            headerTextColor: '#ffc764',
            boxesOutlineColor: '#d4af37',
            boxesBackgroundColor: 'transparent'
          }
        }
      });

      navigate(`/manage/display-builder/${response.id}`);
    } catch (error) {
      console.error('Error creating layout:', error);
      alert('Failed to create layout');
    }
  };

  const deleteLayout = async (layoutId) => {
    if (!window.confirm('Are you sure you want to delete this layout?')) return;

    try {
      await api.delete(`/shul/display-layouts/${layoutId}/`);
      loadLayouts();
    } catch (error) {
      console.error('Error deleting layout:', error);
      alert('Failed to delete layout');
    }
  };

  const duplicateLayout = async (layout) => {
    try {
      const newName = prompt(`Enter a name for the duplicate:`, `${layout.name} (Copy)`);
      if (!newName) return;

      const response = await api.post('/shul/display-layouts/', {
        name: newName,
        layout_config: layout.layout_config
      });

      navigate(`/manage/display-builder/${response.id}`);
    } catch (error) {
      console.error('Error duplicating layout:', error);
      alert('Failed to duplicate layout');
    }
  };

  const setActiveLayout = async (layoutId) => {
    try {
      await api.put(`/shul/display-layouts/${layoutId}/`, {
        is_active: true
      });
      loadLayouts(); // Reload to show updated active status
    } catch (error) {
      console.error('Error setting active layout:', error);
      alert('Failed to set active layout');
    }
  };

  const toggleGlobalTemplate = async (layoutId, currentStatus) => {
    try {
      const action = currentStatus ? 'remove' : 'make';
      if (!window.confirm(`Are you sure you want to ${action} this layout as a global template? ${!currentStatus ? 'All tenants will see this template.' : ''}`)) {
        return;
      }

      await api.post(`/shul/display-layouts/${layoutId}/toggle-global/`);
      loadLayouts(); // Reload to show updated status
      loadGlobalTemplates(); // Reload global templates
      alert(`Layout successfully ${currentStatus ? 'removed from' : 'marked as'} global template`);
    } catch (error) {
      console.error('Error toggling global template:', error);
      alert('Failed to toggle global template status');
    }
  };

  const constrainBoxToCanvas = (box, canvasWidth = 1920, canvasHeight = 1080) => {
    // Ensure box fits within canvas bounds
    const constrainedBox = { ...box };
    const pos = { ...box.position };

    // Constrain width and height
    pos.width = Math.min(pos.width, canvasWidth - 40); // 40px margin
    pos.height = Math.min(pos.height, canvasHeight - 40);

    // Constrain position
    pos.x = Math.max(0, Math.min(pos.x, canvasWidth - pos.width));
    pos.y = Math.max(0, Math.min(pos.y, canvasHeight - pos.height));

    constrainedBox.position = pos;
    return constrainedBox;
  };

  const createFromTemplate = async (template) => {
    try {
      const layoutName = prompt(`Enter a name for your new layout:`, template.name);
      if (!layoutName) return;

      // Constrain all boxes to fit within 1920x1080 canvas
      const constrainedLayout = {
        ...template.layout,
        boxes: template.layout.boxes.map(box => constrainBoxToCanvas(box))
      };

      const response = await api.post('/shul/display-layouts/', {
        name: layoutName,
        layout_config: constrainedLayout
      });

      setShowTemplates(false);
      navigate(`/manage/display-builder/${response.id}`);
    } catch (error) {
      console.error('Error creating layout from template:', error);
      alert('Failed to create layout from template');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f5f7fa' }}>
        <div style={{ fontSize: '18px', color: '#2c3e50' }}>Loading layouts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f5f7fa' }}>
        <div style={{ fontSize: '18px', color: '#e74c3c' }}>{error}</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa', padding: '40px 20px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
          <div>
            <h1 style={{ fontSize: '32px', fontWeight: '700', color: '#2c3e50', margin: 0 }}>Display Layouts</h1>
            <p style={{ fontSize: '16px', color: '#7f8c8d', marginTop: '8px' }}>
              Create and manage custom display layouts for your synagogue
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setShowTemplates(true)}
              style={{
                padding: '14px 28px',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                boxShadow: '0 2px 8px rgba(52, 152, 219, 0.3)'
              }}
            >
              📋 Choose Template
            </button>
            <button
              onClick={createNewLayout}
              style={{
                padding: '14px 28px',
                backgroundColor: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                boxShadow: '0 2px 8px rgba(39, 174, 96, 0.3)'
              }}
            >
              + Create Blank Layout
            </button>
          </div>
        </div>

        {/* Layouts Grid */}
        {layouts.length === 0 ? (
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '60px 40px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>📺</div>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#2c3e50', marginBottom: '12px' }}>
              No layouts yet
            </h2>
            <p style={{ fontSize: '16px', color: '#7f8c8d', marginBottom: '24px' }}>
              Create your first custom display layout to get started
            </p>
            <button
              onClick={createNewLayout}
              style={{
                padding: '12px 24px',
                backgroundColor: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              Create Your First Layout
            </button>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '24px'
          }}>
            {layouts.map(layout => (
              <div
                key={layout.id}
                style={{
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: layout.is_active
                    ? '0 4px 16px rgba(39, 174, 96, 0.3)'
                    : '0 2px 8px rgba(0,0,0,0.1)',
                  border: layout.is_active ? '3px solid #27ae60' : '3px solid transparent',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = layout.is_active
                    ? '0 8px 20px rgba(39, 174, 96, 0.4)'
                    : '0 8px 16px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = layout.is_active
                    ? '0 4px 16px rgba(39, 174, 96, 0.3)'
                    : '0 2px 8px rgba(0,0,0,0.1)';
                }}
              >
                {/* Preview Area */}
                <div
                  onClick={() => navigate(`/manage/display-builder/${layout.id}`)}
                  style={{
                    height: '180px',
                    backgroundColor: '#000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottom: '1px solid #e1e8ed',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* Preview wrapper - 320x180 container that clips scaled content */}
                  <div style={{
                    width: '320px',
                    height: '180px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {/* Mini Preview - 1920x1080 scaled down by 0.167 */}
                    <div style={{
                      width: '1920px',
                      height: '1080px',
                      backgroundColor: layout.layout_config?.globalSettings?.backgroundType === 'color'
                        ? layout.layout_config.globalSettings.backgroundColor
                        : '#000',
                      backgroundImage: layout.layout_config?.globalSettings?.backgroundType === 'image' && layout.layout_config.globalSettings.backgroundImage
                        ? `url(${layout.layout_config.globalSettings.backgroundImage})`
                        : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      position: 'absolute',
                      transform: 'scale(0.167)',
                      transformOrigin: 'top left',
                      left: 0,
                      top: 0
                    }}>
                      {/* Header */}
                      <div style={{
                        height: '84px',
                        backgroundColor: layout.layout_config?.globalSettings?.headerBgColor || '#162A45',
                        borderBottom: '2px solid ' + (layout.layout_config?.globalSettings?.boxesOutlineColor || '#d4af37')
                      }} />

                      {/* Boxes */}
                      <div style={{
                        position: 'relative',
                        width: '1880px',
                        height: '920px',
                        margin: '20px'
                      }}>
                        {layout.layout_config?.boxes?.map((box, idx) => (
                          <div
                            key={idx}
                            style={{
                              position: 'absolute',
                              left: box.position.x,
                              top: box.position.y,
                              width: box.position.width,
                              height: box.position.height,
                              border: box.styling.showBorder ? `3px solid ${box.styling.borderColor}` : 'none',
                              backgroundColor: (() => {
                                const color = box.styling.backgroundColor || 'transparent';
                                const opacity = box.styling.backgroundOpacity ?? 100;
                                if (!color || color === 'transparent' || opacity === 100) return color;
                                const hex = color.startsWith('#') ? color : '#000000';
                                const r = parseInt(hex.slice(1, 3), 16);
                                const g = parseInt(hex.slice(3, 5), 16);
                                const b = parseInt(hex.slice(5, 7), 16);
                                return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
                              })(),
                              borderRadius: '12px'
                            }}
                          />
                        ))}
                      </div>

                      {/* Footer */}
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '36px',
                        backgroundColor: layout.layout_config?.globalSettings?.headerBgColor || '#162A45',
                        borderTop: '2px solid ' + (layout.layout_config?.globalSettings?.boxesOutlineColor || '#d4af37')
                      }} />
                    </div>
                  </div>

                  {layout.is_active && (
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      padding: '8px 16px',
                      backgroundColor: '#27ae60',
                      color: 'white',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '700',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      zIndex: 10
                    }}>
                      ✓ ACTIVE
                    </div>
                  )}
                </div>

                {/* Info Area */}
                <div style={{ padding: '20px' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#2c3e50',
                    marginBottom: '8px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {layout.name}
                  </h3>
                  {layout.is_active && (
                    <p style={{
                      fontSize: '13px',
                      color: '#27ae60',
                      fontWeight: '700',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span style={{ fontSize: '16px' }}>●</span> Currently Active on Display
                    </p>
                  )}
                  <p style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '16px' }}>
                    {layout.layout_config?.boxes?.length || 0} boxes
                  </p>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/manage/display-builder/${layout.id}`);
                      }}
                      style={{
                        flex: 1,
                        padding: '8px 16px',
                        backgroundColor: '#f97316',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      Edit
                    </button>
                    {!layout.is_active && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveLayout(layout.id);
                        }}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#27ae60',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                      >
                        Set Active
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateLayout(layout);
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#95a5a6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      Duplicate
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteLayout(layout.id);
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      Delete
                    </button>
                    {isMasterAdmin && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleGlobalTemplate(layout.id, layout.is_global_template);
                        }}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: layout.is_global_template ? '#f39c12' : '#3498db',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                      >
                        {layout.is_global_template ? '⭐ Global Template' : 'Make Global'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Template Selection Modal */}
        {showTemplates && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '1200px',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#2c3e50', margin: 0 }}>
                  Choose a Template
                </h2>
                <button
                  onClick={() => setShowTemplates(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Close
                </button>
              </div>

              {/* Built-in Templates Section */}
              {LAYOUT_TEMPLATES.length > 0 && (
                <>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginTop: '20px', marginBottom: '16px' }}>
                    Built-in Templates
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '24px',
                    marginBottom: '32px'
                  }}>
                    {LAYOUT_TEMPLATES.map(template => (
                  <div
                    key={template.id}
                    onClick={() => createFromTemplate(template)}
                    style={{
                      backgroundColor: '#f8f9fa',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      border: '2px solid #e1e8ed'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {/* Template Preview */}
                    <div style={{
                      height: '180px',
                      backgroundColor: '#000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderBottom: '2px solid #e1e8ed',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      {/* Preview wrapper - 320x180 container that clips scaled content */}
                      <div style={{
                        width: '320px',
                        height: '180px',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        {/* Mini Preview - 1920x1080 scaled down by 0.167 */}
                        <div style={{
                          width: '1920px',
                          height: '1080px',
                          backgroundColor: template.layout?.globalSettings?.backgroundType === 'color'
                            ? template.layout.globalSettings.backgroundColor
                            : '#000',
                          backgroundImage: template.layout?.globalSettings?.backgroundType === 'image' && template.layout.globalSettings.backgroundImage
                            ? `url(${template.layout.globalSettings.backgroundImage})`
                            : 'none',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          position: 'absolute',
                          transform: 'scale(0.167)',
                          transformOrigin: 'top left',
                          left: 0,
                          top: 0
                        }}>
                          {/* Header */}
                          <div style={{
                            height: '84px',
                            backgroundColor: template.layout?.globalSettings?.headerBgColor || '#162A45',
                            borderBottom: '2px solid ' + (template.layout?.globalSettings?.boxesOutlineColor || '#d4af37')
                          }} />

                          {/* Boxes */}
                          <div style={{
                            position: 'relative',
                            width: '1880px',
                            height: '920px',
                            margin: '20px'
                          }}>
                            {template.layout?.boxes?.map((box, idx) => (
                              <div
                                key={idx}
                                style={{
                                  position: 'absolute',
                                  left: box.position.x,
                                  top: box.position.y,
                                  width: box.position.width,
                                  height: box.position.height,
                                  border: box.styling.showBorder ? `3px solid ${box.styling.borderColor}` : 'none',
                                  backgroundColor: (() => {
                                    const color = box.styling.backgroundColor || 'transparent';
                                    const opacity = box.styling.backgroundOpacity ?? 100;
                                    if (!color || color === 'transparent' || opacity === 100) return color;
                                    const hex = color.startsWith('#') ? color : '#000000';
                                    const r = parseInt(hex.slice(1, 3), 16);
                                    const g = parseInt(hex.slice(3, 5), 16);
                                    const b = parseInt(hex.slice(5, 7), 16);
                                    return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
                                  })(),
                                  borderRadius: '12px'
                                }}
                              />
                            ))}
                          </div>

                          {/* Footer */}
                          <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '36px',
                            backgroundColor: template.layout?.globalSettings?.headerBgColor || '#162A45',
                            borderTop: '2px solid ' + (template.layout?.globalSettings?.boxesOutlineColor || '#d4af37')
                          }} />
                        </div>
                      </div>
                    </div>

                    {/* Template Info */}
                    <div style={{ padding: '20px' }}>
                      <h3 style={{
                        fontSize: '20px',
                        fontWeight: '600',
                        color: '#2c3e50',
                        marginBottom: '8px'
                      }}>
                        {template.name}
                      </h3>
                      <p style={{
                        fontSize: '14px',
                        color: '#7f8c8d',
                        marginBottom: '12px',
                        lineHeight: '1.5'
                      }}>
                        {template.description}
                      </p>
                      <div style={{
                        fontSize: '12px',
                        color: '#95a5a6',
                        fontWeight: '600'
                      }}>
                        {template.layout.boxes.length} boxes included
                      </div>
                    </div>
                  </div>
                ))}
                  </div>
                </>
              )}

              {/* Global Templates Section (from admin) */}
              {globalTemplates.length > 0 && (
                <>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginTop: '20px', marginBottom: '16px' }}>
                    Custom Templates
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '24px'
                  }}>
                    {globalTemplates.map(template => {
                      const templateData = {
                        id: template.id,
                        name: template.name,
                        description: `Custom template by admin`,
                        layout: template.layout_config
                      };

                      return (
                        <div
                          key={`global-${template.id}`}
                          onClick={() => createFromTemplate(templateData)}
                          style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            border: '2px solid #f39c12'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 8px 20px rgba(243, 156, 18, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          {/* Template Preview (same as built-in) */}
                          <div style={{
                            height: '180px',
                            backgroundColor: '#000',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderBottom: '2px solid #f39c12',
                            position: 'relative',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: '320px',
                              height: '180px',
                              position: 'relative',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: '1920px',
                                height: '1080px',
                                backgroundColor: template.layout_config?.globalSettings?.backgroundType === 'color'
                                  ? template.layout_config.globalSettings.backgroundColor
                                  : '#000',
                                backgroundImage: template.layout_config?.globalSettings?.backgroundType === 'image' && template.layout_config.globalSettings.backgroundImage
                                  ? `url(${template.layout_config.globalSettings.backgroundImage})`
                                  : 'none',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                position: 'absolute',
                                transform: 'scale(0.167)',
                                transformOrigin: 'top left',
                                left: 0,
                                top: 0
                              }}>
                                <div style={{
                                  height: '84px',
                                  backgroundColor: template.layout_config?.globalSettings?.headerBgColor || '#162A45',
                                  borderBottom: '2px solid ' + (template.layout_config?.globalSettings?.boxesOutlineColor || '#d4af37')
                                }} />

                                <div style={{
                                  position: 'relative',
                                  width: '1880px',
                                  height: '920px',
                                  margin: '20px'
                                }}>
                                  {template.layout_config?.boxes?.map((box, idx) => (
                                    <div
                                      key={idx}
                                      style={{
                                        position: 'absolute',
                                        left: box.position.x,
                                        top: box.position.y,
                                        width: box.position.width,
                                        height: box.position.height,
                                        border: box.styling.showBorder ? `3px solid ${box.styling.borderColor}` : 'none',
                                        backgroundColor: (() => {
                                          const color = box.styling.backgroundColor || 'transparent';
                                          const opacity = box.styling.backgroundOpacity ?? 100;
                                          if (!color || color === 'transparent' || opacity === 100) return color;
                                          const hex = color.startsWith('#') ? color : '#000000';
                                          const r = parseInt(hex.slice(1, 3), 16);
                                          const g = parseInt(hex.slice(3, 5), 16);
                                          const b = parseInt(hex.slice(5, 7), 16);
                                          return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
                                        })(),
                                        borderRadius: '12px'
                                      }}
                                    />
                                  ))}
                                </div>

                                <div style={{
                                  position: 'absolute',
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  height: '36px',
                                  backgroundColor: template.layout_config?.globalSettings?.headerBgColor || '#162A45',
                                  borderTop: '2px solid ' + (template.layout_config?.globalSettings?.boxesOutlineColor || '#d4af37')
                                }} />
                              </div>
                            </div>
                          </div>

                          {/* Template Info */}
                          <div style={{ padding: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <h3 style={{
                                fontSize: '20px',
                                fontWeight: '600',
                                color: '#2c3e50',
                                margin: 0
                              }}>
                                {template.name}
                              </h3>
                              <span style={{
                                fontSize: '11px',
                                backgroundColor: '#f39c12',
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontWeight: '600'
                              }}>
                                ADMIN
                              </span>
                            </div>
                            <p style={{
                              fontSize: '14px',
                              color: '#7f8c8d',
                              marginBottom: '12px',
                              lineHeight: '1.5'
                            }}>
                              Custom template created by admin
                            </p>
                            <div style={{
                              fontSize: '12px',
                              color: '#95a5a6',
                              fontWeight: '600'
                            }}>
                              {template.layout_config?.boxes?.length || 0} boxes included
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LayoutManager;
